import { useState } from 'react';
import { ArrowLeft, Trophy, Zap } from 'lucide-react';
import {
  banepadScoreText,
  banepadFormatLabel,
  getTournamentType,
  TOURNAMENT_TYPES,
  calcSeriespillStandings,
  computeKMClassStandings,
  calcAmericanoStandings,
} from '../../engine';
import { Padel24GroupBoard } from './Padel24GroupBoard';

const AMERICANO_FAMILY = [
  'americano',
  'mexicano',
  'mixicano',
  'team_mexicano',
  'mix_americano',
  'club_americano',
  'club_team_americano',
  'club_mexicano',
];

export interface TurneringsDashboardProps {
  courts: number[];
  /** Resolve a court's live status (the same statusOf the central grid uses). */
  statusFor: (court: number) => any;
  courtName: (court: number) => string;
  clock: string;
  allPlayers?: any[];
  /** The active tournament + paused ones + the active series — the selectable events. */
  tournament?: any;
  pausedTournaments?: any[];
  activeSeries?: any;
  /** Back to the central screen. */
  onBack: () => void;
  /** Open a court's full pad. */
  onOpenCourt?: (court: number) => void;
  /**
   * Show THIS event's dashboard, even if it is not currently in play — opened
   * from the per-tournament «Dashboard» button in the Central's list. Without it
   * the board defaults to whatever is running.
   */
  focusEventId?: any;
}

// ── Turneringsdashboard ──
// Tournament-centric big-screen overview: a «Spilles nå» board (the matches being
// played right now, with live score — NOT a per-court grid) and the STANDINGS for
// the active tournament/series. Normally one event runs at a time; if several are
// active you pick which one to show. Switched to from the Banepad central screen.
export function TurneringsDashboard({
  courts,
  statusFor,
  courtName,
  clock,
  allPlayers = [],
  tournament = null,
  pausedTournaments = [],
  activeSeries = null,
  onBack,
  onOpenCourt,
  focusEventId = null,
}: TurneringsDashboardProps) {
  // Selectable events. This is a LIVE board, so only tournaments that are
  // actually in play belong here — `pausedTournaments` also holds finished and
  // archived ones, which previously showed up as pickable "historical" events.
  // Same status set the Central screen uses for its pad list.
  const LIVE_STATUS = ['ready', 'running', 'playing', 'finals'];
  const idOf = (ev: any) => ev?.id || ev?.displayId;
  // …unless one was explicitly opened from the tournament list, which is how you
  // reach the board for an event that has finished or not started yet.
  const isLive = (ev: any) =>
    (focusEventId != null && idOf(ev) === focusEventId) ||
    ev === activeSeries ||
    (LIVE_STATUS.includes(ev?.status) && !ev?.isArchived);
  // A forced tournament can appear both as `tournament` and in pausedTournaments — dedupe.
  const seenEv = new Set<any>();
  const events = [tournament, ...(pausedTournaments || []), ...(activeSeries ? [activeSeries] : [])]
    .filter(Boolean)
    .filter(isLive)
    .map((ev: any) => {
      const isSeries = ev === activeSeries;
      const tt = isSeries ? TOURNAMENT_TYPES.seriespill : getTournamentType(ev.config);
      // Use the NORMALISED type id (getTournamentType resolves config.tournamentType
      // OR config.type, falling back to 'padel24' for legacy tournaments).
      const kind = isSeries
        ? 'series'
        : tt.id === 'padel24'
          ? 'padel24'
          : AMERICANO_FAMILY.includes(tt.id)
            ? 'americano'
            : 'pairs';
      return { id: ev.id || ev.displayId, name: ev.config?.name || tt.label, kind, obj: ev, tt };
    })
    .filter((e) => {
      if (seenEv.has(e.id)) return false;
      seenEv.add(e.id);
      return true;
    });
  const [selectedId, setSelectedId] = useState<any>(focusEventId ?? events[0]?.id ?? null);
  const selected = events.find((e) => e.id === selectedId) || events[0] || null;
  // An explicitly opened event may be finished or not started — the live-only
  // parts («Pågår», «På banene») must not claim otherwise.
  const selectedIsLive =
    !!selected &&
    (selected.obj === activeSeries ||
      (LIVE_STATUS.includes(selected.obj?.status) && !selected.obj?.isArchived));

  // Unified player-name lookup. Standings keys and match player ids can be EITHER
  // strings or numbers depending on the source (Padel24 standings keys come back
  // as strings while `players[].id` are numbers) — so key the map by String(id)
  // and look up the same way. Covers every event's own roster plus the global DB.
  const playerNameMap = new Map<string, string>();
  allPlayers.forEach((p: any) => p && playerNameMap.set(String(p.id), p.name));
  events.forEach((e) =>
    (e.obj.players || []).forEach((p: any) => p && playerNameMap.set(String(p.id), p.name)),
  );
  const nameOf = (pid: any) => playerNameMap.get(String(pid)) || '';
  const firstName = (pid: any) => nameOf(pid).split(' ')[0];

  // Padel24's brand colour (#c2f02f) is a very light lime — near-illegible as
  // text on white, or as white text on top of it. Derive readable variants: a
  // darkened version for coloured text/score numbers, and a luminance-based
  // on-colour text (dark on light accents, white on dark ones).
  const darken = (hex: string, keep = 0.42) => {
    const h = (hex || '').replace('#', '');
    if (h.length < 6) return hex;
    const c = (i: number) => Math.round(parseInt(h.slice(i, i + 2), 16) * keep);
    return `rgb(${c(0)}, ${c(2)}, ${c(4)})`;
  };
  const readableOn = (hex: string) => {
    const h = (hex || '').replace('#', '');
    if (h.length < 6) return '#fff';
    const lum =
      (0.299 * parseInt(h.slice(0, 2), 16) +
        0.587 * parseInt(h.slice(2, 4), 16) +
        0.114 * parseInt(h.slice(4, 6), 16)) /
      255;
    return lum > 0.6 ? '#1a2e05' : '#fff';
  };

  // A best-effort division/class name lookup (for events split into divisions).
  const divNameOf = (obj: any, divId: any) => {
    const all = [
      ...(obj.divisions || []),
      ...((obj.classes || []) as any[]).flatMap((c: any) => c.divisions || []),
    ];
    return all.find((d: any) => d.id === divId)?.name || '';
  };

  // Standings tables for the selected event (one per division for pair formats).
  const tablesFor = (ev: any): any[] => {
    if (!ev) return [];
    const obj = ev.obj;
    // Name lookup (handles string/number id mismatch + every event roster).
    const pName = (pid: any) => nameOf(pid) || 'Spiller';
    if (ev.kind === 'americano') {
      // calcAmericanoStandings returns the list ALREADY RANKED by the
      // tournament's own ranking order (wins first by default), with injured
      // players at the bottom and rank null. Keep that order: re-sorting on
      // points here showed a different leader on the big screen than in the
      // app whenever the tournament ranked wins first.
      const rows = (calcAmericanoStandings(obj) || [])
        .filter((s: any) => !s.injured && s.rank != null)
        .map((s: any) => ({
          name: pName(s.playerId),
          played: s.gamesPlayed || 0,
          metric: Math.round(s.points || 0),
        }));
      return [{ title: '', metricLabel: 'Poeng', rows }];
    }
    if (ev.kind === 'padel24') {
      // Padel24 carries a precomputed player leaderboard (standings map). Fall
      // back to the full player roster (groups → players, or obj.players) so the
      // table renders even before any results are in.
      const st = obj.standings || {};
      const ids = Object.keys(st).length
        ? Object.keys(st)
        : (obj.groups || [])
            .flatMap((g: any) => g.players || [])
            .map((p: any) => (typeof p === 'string' ? p : p.id))
            .concat((obj.players || []).map((p: any) => p.id));
      const rows = [...new Set(ids)]
        .map((pid: any) => {
          const s = st[pid] || {};
          return {
            name: pName(pid),
            played: s.matchesPlayed || 0,
            // Padel24's own leaderboard ranks by points (games won) — mirror it.
            metric: s.points || 0,
            tiebreak: s.wins || 0,
          };
        })
        .sort((a, b) => b.metric - a.metric || b.tiebreak - a.tiebreak);
      return [{ title: '', metricLabel: 'Poeng', rows }];
    }
    const divIds = [...new Set((obj.pairs || []).map((p: any) => p.divisionId).filter(Boolean))];
    const ids = divIds.length ? divIds : [null];
    return ids.map((divId) => {
      let rows: any[];
      if (ev.kind === 'series') {
        const dp = (obj.pairs || []).filter((p: any) => divId == null || p.divisionId === divId);
        const dm = (obj.matches || []).filter((m: any) => divId == null || m.divisionId === divId);
        rows = (calcSeriespillStandings(dp, dm) || []).map((r: any) => ({
          name: `${r.pair?.p1Name || '?'}/${r.pair?.p2Name || '?'}`,
          played: r.played,
          metric: r.points,
        }));
      } else {
        rows = (computeKMClassStandings(obj, divId) || []).map((r: any) => ({
          name: `${r.p1Name}/${r.p2Name}`,
          played: r.played,
          metric: r.wins,
        }));
      }
      return {
        title: ids.length > 1 ? divNameOf(obj, divId) || String(divId) : '',
        metricLabel: ev.kind === 'series' ? 'Poeng' : 'Seire',
        rows,
      };
    });
  };

  // Resolve a tournament/series match's two team names from the events' pairs.
  const allPairs = events.flatMap((e) => e.obj.pairs || []);
  const pairLabel = (pairId: any) => {
    const p = allPairs.find((x: any) => x.id === pairId);
    return p ? `${firstName(p.p1Id)}/${firstName(p.p2Id)}` : null;
  };
  // Pair-based (KM/serie) matches carry team.pairId; player-based (Padel24/
  // Americano) carry player ids directly. Resolve either.
  const teamLabel = (team: any) => {
    if (team?.pairId) return pairLabel(team.pairId);
    // Player-based formats: ids may be flat (p1Id/p2Id, players[]) OR nested under
    // concrete-player wrappers (Padel24: team.p1.playerId / team.p2.playerId).
    const ids = (
      team?.players || [team?.p1Id ?? team?.p1?.playerId, team?.p2Id ?? team?.p2?.playerId]
    ).filter((v: any) => v != null);
    const names = ids.map((id: any) => firstName(id)).filter(Boolean);
    return names.length ? names.join('/') : null;
  };
  const teamsLabel = (m: any) => {
    const a = teamLabel(m?.team1);
    const b = teamLabel(m?.team2);
    return a && b ? `${a} vs ${b}` : null;
  };

  // «På banene» — every court that has a match on it (the resolver's current
  // OR, for a forced/upcoming event, the next match). Enkeltkamp shows the four
  // players; a tournament/serie match shows the two pairs + live/final score.
  const liveNow = courts
    .map((court) => ({ court, s: statusFor(court) }))
    .filter(({ s }) => s.mode === 'tournament' && (s.current || s.next || s.prev))
    .map(({ court, s }) => {
      // Forced onto the court from the Central → treat as "playing now" regardless
      // of the clock (the whole point of forcing is to show it NOW).
      const forced = !!s.forced;
      if (s.source === 'single_match') {
        const live = !!s.current || forced;
        const c = (s.activity?.registrations || []).filter((r: any) => r.status === 'confirmed');
        const four = c.length >= 4;
        const sets = (s.activity?.liveScore?.sets || []).filter((x: any) => x.t1 || x.t2);
        return {
          court,
          label: 'Enkeltkamp',
          accent: '#0ea5e9',
          teamA: four ? `${firstName(c[0].playerId)}/${firstName(c[1].playerId)}` : 'Enkeltkamp',
          teamB: four ? `${firstName(c[2].playerId)}/${firstName(c[3].playerId)}` : null,
          sub: live ? 'Pågår' : 'Neste',
          score: sets.length ? sets.map((x: any) => `${x.t1}-${x.t2}`).join('  ') : '0-0',
        };
      }
      // Prefer a match with CONCRETE teams (current → next → prev). Between rounds
      // the next match can be an undrawn placeholder with no players yet — fall
      // back to the last completed match so the card stays meaningful instead of
      // showing the bare tournament name.
      const m =
        [s.current, s.next, s.prev].find((mm: any) => mm && teamsLabel(mm)) || s.current || s.next;
      const isCurrent = !!s.current && m === s.current;
      const isPrev = m === s.prev && m !== s.current && m !== s.next;
      const tt = TOURNAMENT_TYPES[s.formatType] || null;
      // Prefer the in-progress live score (persisted continuously) over the
      // finalised score. KM/serie store sets; Padel24/Americano store {score1,score2}.
      const lsObj = m?.liveScore;
      const lsSets = (lsObj?.sets || []).filter((x: any) => x.t1 || x.t2);
      const liveScore = lsSets.length
        ? lsSets.map((x: any) => `${x.t1}-${x.t2}`).join('  ')
        : lsObj?.score1 != null
          ? `${lsObj.score1}-${lsObj.score2 ?? 0}`
          : null;
      // Final score: KM/serie keep it under m.score (sets); Padel24/Americano keep
      // it as top-level m.score1/m.score2.
      const finalScore =
        banepadScoreText(m?.score) || (m?.score1 != null ? `${m.score1}-${m.score2 ?? 0}` : null);
      const teamA = teamLabel(m?.team1);
      const teamB = teamLabel(m?.team2);
      return {
        court,
        label: banepadFormatLabel(s.formatType) || s.name || 'Turnering',
        accent: tt?.color || '#65a30d',
        teamA: teamA || 'Venter på neste runde',
        teamB: teamA && teamB ? teamB : null,
        sub: isPrev ? 'Nylig' : isCurrent || forced ? 'Pågår' : 'Neste',
        score: liveScore || finalScore || (isPrev ? '' : '–'),
      };
    });

  const tables = tablesFor(selected);

  // ── Live key figures for the selected event ──
  // Everything is derived from the match list, so it stays correct per format:
  // a series match counts as played when it has a score, a tournament match when
  // it is completed. Byes are excluded from the "played" pace — they were never
  // actually on court — but still count towards the total.
  const stats = (() => {
    if (!selected) return null;
    const obj = selected.obj;
    const isSeries = selected.kind === 'series';
    const all = obj.matches || [];
    const done = (m: any) => (isSeries ? !!(m.score || m.completed) : !!m.completed);
    const played = all.filter(done);
    const realPlayed = played.filter((m: any) => !m.bye && !m.score?.bye);
    const pct = all.length ? Math.round((played.length / all.length) * 100) : 0;
    // Games/points actually recorded — a cheap "how much padel has been played".
    let games = 0;
    realPlayed.forEach((m: any) => {
      const s = m.score;
      if (!s) return;
      if (Array.isArray(s.sets)) s.sets.forEach((x: any) => (games += (x.t1 || 0) + (x.t2 || 0)));
      else if (s.t1 != null) games += (s.t1 || 0) + (s.t2 || 0);
      if (m.score1 != null) games += (m.score1 || 0) + (m.score2 || 0);
    });
    const courtsBusy = courts.filter((c) => {
      const s = statusFor(c);
      return s.mode === 'tournament' && (s.current || s.forced);
    }).length;
    const roster = obj.players?.length || 0;
    const pairs = obj.pairs?.length || 0;
    const phase = obj.phase ? `${obj.phase}/${obj.phasesTotal || obj.phase}` : null;
    // Leader = top row of the first standings table we can build.
    const leader = tables.find((t: any) => t.rows?.length)?.rows?.[0] || null;
    return {
      total: all.length,
      played: played.length,
      pct,
      games,
      courtsBusy,
      roster,
      pairs,
      phase,
      leader,
    };
  })();

  // A compact stat tile — label above, big value, optional sub-line.
  const statTile = (label: string, value: any, sub?: any, accent?: string) => (
    <div
      className="rounded-xl px-3 py-2.5 min-w-0"
      style={{ background: '#fff', border: '1px solid #e5e7eb' }}
    >
      <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3 truncate">
        {label}
      </div>
      <div
        className="font-display font-bold tabular-nums truncate"
        style={{ fontSize: 22, color: accent || '#0f172a', lineHeight: 1.15 }}
      >
        {value}
      </div>
      {sub ? <div className="text-[10px] text-ink-3 truncate">{sub}</div> : null}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-ink-2 hover:text-ink-1 transition-colors font-mono text-xs uppercase tracking-wider"
          >
            <ArrowLeft size={15} /> Sentral
          </button>
          <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-ink-3">
            <Trophy size={13} /> Turneringsdashboard
          </span>
        </div>
        <span
          className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider"
          style={{ color: '#0e7490' }}
        >
          <span className="relative flex h-2 w-2">
            <span
              className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
              style={{ background: '#22d3ee' }}
            ></span>
            <span
              className="relative inline-flex rounded-full h-2 w-2"
              style={{ background: '#06b6d4' }}
            ></span>
          </span>
          Live · {clock}
        </span>
      </div>

      {/* Key figures for the running event — the "at a glance" row. */}
      {selected && stats && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded"
              style={{ background: selected.tt.color, color: readableOn(selected.tt.color) }}
            >
              {selected.tt.abbrev}
            </span>
            <span className="text-[15px] font-semibold text-ink-1">{selected.name}</span>
            {selectedIsLive ? (
              <span
                className="font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac' }}
              >
                ● Pågår
              </span>
            ) : (
              <span
                className="font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' }}
              >
                {selected.obj?.status === 'done' ? 'Ferdig' : 'Ikke i gang'}
              </span>
            )}
          </div>
          {/* Progress — the single most useful number on a big screen. */}
          <div
            className="rounded-xl p-3"
            style={{ background: '#fff', border: '1px solid #e5e7eb' }}
          >
            <div className="flex items-baseline justify-between gap-2 mb-1.5">
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
                Kamper spilt
              </span>
              <span className="font-mono text-[11px] text-ink-2 tabular-nums">
                {stats.played}/{stats.total} · {stats.pct}%
              </span>
            </div>
            <div
              className="rounded-full overflow-hidden"
              style={{ height: 8, background: '#eef2f7' }}
            >
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${stats.pct}%`, background: darken(selected.tt.color, 0.62) }}
              />
            </div>
          </div>
          <div
            className="grid gap-2"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}
          >
            {statTile('Baner i spill', `${stats.courtsBusy}/${courts.length}`)}
            {stats.phase ? statTile('Fase', stats.phase) : null}
            {statTile(
              selected.kind === 'pairs' || selected.kind === 'series' ? 'Par' : 'Spillere',
              selected.kind === 'pairs' || selected.kind === 'series' ? stats.pairs : stats.roster,
            )}
            {statTile('Gjenstår', stats.total - stats.played, 'kamper')}
            {stats.games ? statTile('Games spilt', stats.games) : null}
            {stats.leader
              ? statTile('Leder', stats.leader.name, `${stats.leader.metric}`, '#3f6212')
              : null}
          </div>
        </div>
      )}

      {/* På banene — only meaningful for something actually in play. A dashboard
          opened for a finished/not-started event shows its figures + standings. */}
      <div style={{ display: selectedIsLive ? undefined : 'none' }}>
        <div className="font-mono text-[11px] uppercase tracking-wider text-ink-3 mb-2 flex items-center gap-1.5">
          <Zap size={12} /> På banene
        </div>
        {liveNow.length === 0 ? (
          <div
            className="rounded-xl p-6 text-center text-ink-3 text-sm"
            style={{ background: '#fff', border: '1px solid #e5e7eb' }}
          >
            Ingen kamper på banene akkurat nå.
          </div>
        ) : (
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}
          >
            {liveNow.map((m) => (
              <button
                key={m.court}
                onClick={() => onOpenCourt?.(m.court)}
                className="text-left rounded-xl p-4 transition-all hover:shadow-md"
                style={{
                  background: '#fff',
                  borderLeft: `4px solid ${m.accent}`,
                  border: `1px solid ${m.accent}33`,
                }}
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span
                    className="font-mono text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                    style={{ background: m.accent + '2e', color: darken(m.accent) }}
                  >
                    {m.label}
                  </span>
                  <span className="font-mono text-[11px] text-ink-3">
                    {courtName(m.court)}
                    {m.sub ? ` · ${m.sub}` : ''}
                  </span>
                </div>
                {m.teamB ? (
                  // Two teams → score sits BETWEEN them: «Lag A — score — Lag B».
                  <div className="flex items-center gap-2">
                    <span
                      className="font-display font-semibold text-ink-1 truncate flex-1 text-right"
                      style={{ fontSize: 16 }}
                    >
                      {m.teamA}
                    </span>
                    <span
                      className="font-mono font-bold tabular-nums flex-shrink-0 px-1.5"
                      style={{ fontSize: 20, color: darken(m.accent) }}
                    >
                      {m.score}
                    </span>
                    <span
                      className="font-display font-semibold text-ink-1 truncate flex-1"
                      style={{ fontSize: 16 }}
                    >
                      {m.teamB}
                    </span>
                  </div>
                ) : (
                  // Single label (placeholder / «Venter på neste runde») → score right.
                  <div className="flex items-center justify-between gap-3">
                    <span
                      className="font-display font-semibold text-ink-1 truncate"
                      style={{ fontSize: 17 }}
                    >
                      {m.teamA}
                    </span>
                    <span
                      className="font-mono font-bold tabular-nums flex-shrink-0"
                      style={{ fontSize: 22, color: darken(m.accent) }}
                    >
                      {m.score}
                    </span>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Standings for the selected event */}
      <div className="rounded-xl p-3" style={{ background: '#fff', border: '1px solid #e5e7eb' }}>
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <div className="font-mono text-[11px] uppercase tracking-wider text-ink-3 flex items-center gap-1.5">
            <Trophy size={12} /> Stilling
          </div>
          {/* Event selector — only when more than one is active. */}
          {events.length > 1 && (
            <div className="flex items-center gap-1 flex-wrap">
              {events.map((e) => {
                const active = e.id === (selected?.id ?? null);
                return (
                  <button
                    key={e.id}
                    onClick={() => setSelectedId(e.id)}
                    className="font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full transition-colors flex items-center gap-1.5"
                    style={
                      active
                        ? { background: e.tt.color, color: readableOn(e.tt.color), fontWeight: 700 }
                        : { background: '#f3f4f6', color: '#6b7280' }
                    }
                  >
                    {e.tt.abbrev} · {e.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {!selected ? (
          <div className="text-center py-8 text-ink-3 text-sm">
            Ingen aktiv turnering akkurat nå.
          </div>
        ) : (
          <div className="space-y-4">
            {events.length === 1 && (
              <div className="flex items-center gap-2">
                <span
                  className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded"
                  style={{ background: selected.tt.color, color: readableOn(selected.tt.color) }}
                >
                  {selected.tt.abbrev}
                </span>
                <span className="text-[14px] font-semibold text-ink-1">{selected.name}</span>
              </div>
            )}
            {selected.kind === 'padel24' && (selected.obj.groups || []).length ? (
              // Padel24 → per-fasegruppe board (current phase) with each group's
              // mini-standings + its matches laid out systematically.
              <Padel24GroupBoard tournament={selected.obj} nameOf={nameOf} />
            ) : tables.every((t) => t.rows.length === 0) ? (
              <div className="text-center py-6 text-ink-3 text-sm">Ingen stilling ennå.</div>
            ) : (
              tables.map((t, ti) => (
                <div key={ti}>
                  {t.title && (
                    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1">
                      {t.title}
                    </div>
                  )}
                  <div className="overflow-hidden rounded-lg border border-line">
                    <table className="w-full text-[13px]">
                      <thead>
                        <tr className="bg-bg-soft/60 text-ink-3 font-mono text-[10px] uppercase tracking-wider">
                          <th className="text-left px-3 py-1.5 w-8">#</th>
                          <th className="text-left px-2 py-1.5">Lag</th>
                          <th className="text-center px-2 py-1.5 w-12">Sp</th>
                          <th className="text-right px-3 py-1.5 w-16">{t.metricLabel}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {t.rows.slice(0, 10).map((r: any, i: number) => (
                          <tr key={i} className="border-t border-line">
                            <td className="px-3 py-1.5 font-mono text-ink-3">{i + 1}</td>
                            <td className="px-2 py-1.5 text-ink-1 truncate">{r.name}</td>
                            <td className="px-2 py-1.5 text-center font-mono text-ink-2">
                              {r.played}
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono font-bold text-ink-1">
                              {r.metric}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
